<template>
  <div>
    <button class="gs-fab" @click="open" title="Buscar (Ctrl/Cmd + K)" aria-label="Buscar">
      <CIcon icon="cilSearch" />
    </button>

    <Coachmark
      storageKey="global-search"
      target=".gs-fab"
      round
      placement="top-left"
      title="¡Nuevo! Búsqueda global"
      text="Pulsa aquí (o Ctrl / Cmd + K) y escribe para buscar clientes, usuarios y tareas al instante."
    />

    <transition name="gs-fade">
      <div v-if="isOpen" class="gs-overlay" @click.self="close">
        <div class="gs-panel" role="dialog">
          <div class="gs-input-wrap">
            <CIcon icon="cilSearch" class="gs-input-icon" />
            <input
              ref="input"
              v-model="query"
              class="gs-input"
              type="text"
              placeholder="Buscar clientes, usuarios, tareas…"
              @keydown.esc="close"
              @keydown.down.prevent="move(1)"
              @keydown.up.prevent="move(-1)"
              @keydown.enter.prevent="enterSel"
            />
            <span class="gs-esc">esc</span>
          </div>

          <div class="gs-results">
            <div v-if="!query.trim()" class="gs-placeholder">
              Escribe para buscar en <strong>clientes</strong>, <strong>usuarios</strong> y <strong>tareas</strong>…
            </div>

            <template v-else>
              <div v-if="results.length === 0 && !loadingTasks" class="gs-placeholder">Sin resultados</div>

              <div v-for="group in grouped" :key="group.type" class="gs-group">
                <div class="gs-group-title">
                  {{ group.label }}
                  <span v-if="group.type === 'task' && loadingTasks" class="gs-spin"></span>
                </div>
                <div
                  v-for="r in group.items"
                  :key="r.type + '-' + r.id"
                  class="gs-item"
                  :class="{ active: r._idx === activeIndex }"
                  @click="goto(r)"
                  @mouseenter="activeIndex = r._idx"
                >
                  <img v-if="r.avatar" :src="r.avatar" class="gs-avatar" @error="onImgErr($event, r)" />
                  <span v-else class="gs-avatar gs-avatar-ph" :style="{ background: r.color }">{{ r.initials }}</span>
                  <div class="gs-item-body">
                    <div class="gs-item-title">{{ r.title }}</div>
                    <div class="gs-item-sub">{{ r.subtitle }}</div>
                  </div>
                  <span class="gs-tag" :class="'tag-' + r.type">{{ r.typeLabel }}</span>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import { loadIntranetPhotos, intranetPhoto } from '@/plugins/intranetPhotos';
import { tokenMatch } from '@/plugins/search';
import Coachmark from '@/components/Coachmark.vue';

export default {
  name: 'GlobalSearch',
  components: { Coachmark },
  data() {
    return {
      isOpen: false,
      query: '',
      clients: [],
      users: [],
      tasks: [],
      loadedData: false,
      loadingTasks: false,
      taskTimer: null,
      activeIndex: 0,
      baseURL: axios.defaults.baseURL,
    };
  },
  computed: {
    filteredClients() {
      if (!this.query.trim()) return [];
      return this.clients.filter(c =>
        tokenMatch(this.query, [c.firstname, c.lastname, c.cif, c.email, c.cellphone, c.phone])
      ).slice(0, 6);
    },
    filteredUsers() {
      if (!this.query.trim()) return [];
      return this.users.filter(u =>
        tokenMatch(this.query, [u.firstname, u.lastname, u.email])
      ).slice(0, 5);
    },
    results() {
      const list = [];
      this.filteredClients.forEach(c => list.push(this.mapClient(c)));
      this.filteredUsers.forEach(u => list.push(this.mapUser(u)));
      this.tasks.slice(0, 6).forEach(t => list.push(this.mapTask(t)));
      list.forEach((r, i) => { r._idx = i; });
      return list;
    },
    grouped() {
      const groups = [
        { type: 'client', label: 'Clientes', items: [] },
        { type: 'user', label: 'Usuarios', items: [] },
        { type: 'task', label: 'Tareas', items: [] },
      ];
      this.results.forEach(r => {
        const g = groups.find(x => x.type === r.type);
        if (g) g.items.push(r);
      });
      return groups.filter(g => g.items.length);
    },
  },
  watch: {
    query() {
      this.activeIndex = 0;
      clearTimeout(this.taskTimer);
      const q = this.query.trim();
      if (!q) { this.tasks = []; this.loadingTasks = false; return; }
      this.loadingTasks = true;
      this.taskTimer = setTimeout(() => this.searchTasks(q), 280);
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onKey);
  },
  unmounted() {
    window.removeEventListener('keydown', this.onKey);
  },
  methods: {
    norm(s) {
      return String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
    },
    titleCase(str) {
      if (!str) return '';
      return String(str).toLowerCase().replace(/\b\p{L}/gu, c => c.toUpperCase());
    },
    initials(a, b) {
      return ((a || '').substr(0, 1) + (b || '').substr(0, 1)).toUpperCase() || '?';
    },
    colorFor(key) {
      const palette = ['#1e63b3', '#5C6BC0', '#A66E90', '#E17055', '#0984E3', '#6C5CE7', '#00897B'];
      let h = 0;
      const s = String(key || '');
      for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h);
      return palette[Math.abs(h) % palette.length];
    },
    onKey(e) {
      const k = e.key ? e.key.toLowerCase() : '';
      if ((e.metaKey || e.ctrlKey) && k === 'k') {
        e.preventDefault();
        this.isOpen ? this.close() : this.open();
      }
    },
    open() {
      this.isOpen = true;
      this.loadData();
      this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
    },
    close() {
      this.isOpen = false;
    },
    loadData() {
      loadIntranetPhotos();
      if (this.loadedData) return;
      this.loadedData = true;
      axios.get('users').then(r => { this.users = r.data || []; }).catch(() => {});
      axios.get('v1/customers/all_units').then(r => { this.clients = r.data || []; }).catch(() => {});
    },
    searchTasks(q) {
      axios({ url: 'tasks/search', method: 'POST', data: { description: q, user_id: null, user_creator_id: '', date: null, status: '' } })
        .then(resp => { this.tasks = (resp.data && resp.data.tasks) || []; })
        .catch(() => { this.tasks = []; })
        .finally(() => { this.loadingTasks = false; });
    },
    mapClient(c) {
      const img = c.image && c.image !== 'default.png' ? this.baseURL + 'img/profiles/' + c.image : null;
      return {
        type: 'client', typeLabel: 'Cliente', id: c.id,
        title: this.titleCase((c.firstname || '') + ' ' + (c.lastname || '')),
        subtitle: [c.cif, c.cellphone || c.phone, c.email].filter(Boolean).join(' · '),
        avatar: img, initials: this.initials(c.firstname, c.lastname), color: this.colorFor(c.id + 'c'),
      };
    },
    mapUser(u) {
      const img = intranetPhoto(u.email) || (u.image && u.image !== 'default.png' ? this.baseURL + 'img/profiles/' + u.image : null);
      return {
        type: 'user', typeLabel: 'Usuario', id: u.id,
        title: this.titleCase((u.firstname || '') + ' ' + (u.lastname || '')),
        subtitle: u.email || '',
        avatar: img, initials: this.initials(u.firstname, u.lastname), color: this.colorFor(u.id + 'u'),
      };
    },
    mapTask(t) {
      const cust = t.customer ? this.titleCase((t.customer.firstname || '') + ' ' + (t.customer.lastname || '')) : 'Sin cliente';
      return {
        type: 'task', typeLabel: 'Tarea', id: t.id,
        title: t.subject || t.description || ('Tarea #' + t.id),
        subtitle: [cust, t.status].filter(Boolean).join(' · '),
        avatar: null, initials: 'T', color: '#0984E3',
      };
    },
    onImgErr(e, r) {
      e.target.style.display = 'none';
      r.avatar = null;
    },
    move(dir) {
      const n = this.results.length;
      if (!n) return;
      this.activeIndex = (this.activeIndex + dir + n) % n;
    },
    enterSel() {
      const r = this.results[this.activeIndex];
      if (r) this.goto(r);
    },
    goto(r) {
      const routes = { client: 'Editar Cliente', user: 'Editar Usuario', task: 'Editar Tarea' };
      this.close();
      this.$router.push({ name: routes[r.type], params: { id: r.id } });
    },
  },
};
</script>

<style scoped>
.gs-fab {
  position: fixed;
  right: 22px;
  bottom: 22px;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: none;
  background: #1e63b3;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 18px rgba(0, 184, 148, 0.4);
  cursor: pointer;
  z-index: 1040;
  transition: background .15s, transform .1s, box-shadow .15s;
}
.gs-fab:hover { background: #164e91; transform: translateY(-1px); box-shadow: 0 8px 22px rgba(0, 184, 148, 0.5); }
.gs-fab svg { width: 22px; height: 22px; }

.gs-overlay {
  position: fixed;
  inset: 0;
  z-index: 1060;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 12vh 16px 16px;
}
.gs-panel {
  width: 100%;
  max-width: 620px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.35);
  overflow: hidden;
  animation: gs-pop .16s ease;
}
@keyframes gs-pop {
  from { transform: translateY(-10px) scale(.98); opacity: 0; }
  to { transform: translateY(0) scale(1); opacity: 1; }
}

.gs-input-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid #eef0f4;
}
.gs-input-icon { width: 20px; height: 20px; color: #4a95d6; flex: 0 0 auto; }
.gs-input {
  flex: 1 1 auto;
  border: none;
  outline: none;
  font-size: 17px;
  color: #2c3038;
  background: transparent;
}
.gs-input::placeholder { color: #9aa2b1; }
.gs-esc {
  font-size: 11px;
  font-weight: 600;
  color: #9aa2b1;
  background: #f1f3f6;
  padding: 2px 7px;
  border-radius: 6px;
}

.gs-results { max-height: 56vh; overflow-y: auto; padding: 6px; }
.gs-placeholder { text-align: center; color: #9aa2b1; font-size: 13.5px; padding: 26px 12px; }

.gs-group { padding: 4px 0; }
.gs-group-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: #9aa2b1;
  padding: 8px 12px 4px;
}
.gs-spin {
  width: 11px; height: 11px;
  border: 2px solid #cfe9e1;
  border-top-color: #1e63b3;
  border-radius: 50%;
  animation: gs-rot .7s linear infinite;
}
@keyframes gs-rot { to { transform: rotate(360deg); } }

.gs-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  border-radius: 10px;
  cursor: pointer;
}
.gs-item.active { background: #e9fbf5; }
.gs-avatar {
  width: 38px; height: 38px;
  border-radius: 50%;
  object-fit: cover;
  flex: 0 0 auto;
}
.gs-avatar-ph {
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 13px; font-weight: 600;
}
.gs-item-body { flex: 1 1 auto; min-width: 0; }
.gs-item-title {
  font-size: 14px; font-weight: 600; color: #2c3038;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.gs-item-sub {
  font-size: 12px; color: #768192;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.gs-tag {
  flex: 0 0 auto;
  font-size: 10px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .02em;
  padding: 2px 8px; border-radius: 20px;
}
.tag-client { color: #1a5aa3; background: #e9f1fa; }
.tag-user { color: #b0700f; background: #fdf1dc; }
.tag-task { color: #0966b8; background: #e6f2fd; }

.gs-fade-enter-active, .gs-fade-leave-active { transition: opacity .15s; }
.gs-fade-enter-from, .gs-fade-leave-to { opacity: 0; }
</style>
